/**
 * Geo-import router — turns an arbitrary multi-select / drag-drop of files
 * into typed import plans, then executes each plan through the right parser:
 *
 *   .kmz/.kml            → kmzParser (existing)
 *   .geojson/.json       → geoJsonImporter (sniffed at execute time)
 *   .zip with .shp       → shapefileParser.parseShapefileZip
 *   .shp (+.dbf/.prj/…)  → shapefileParser.parseShapefileParts (siblings
 *                          grouped by basename)
 *   .dwg                 → existing server-side CAD chain (importDwgFile →
 *                          cadImportToSiteLayers), adapted into the shared
 *                          review shape with the deterministic CAD SiteLayers
 *                          preserved for the id-based CAD merge path.
 *
 * The accept list is deliberately LOCAL to the map-import surface —
 * `UPLOAD_VALID_EXTS` in lib/uploadValidation.ts guards the document
 * pipeline and must not learn .shp/.dwg.
 */

import type { SiteLayer } from '../../types/site';
import type { ParsedGeoImport, ParsedLayer } from './geojsonToLayers';
import { UPLOAD_MAX_FILE_BYTES } from '../../lib/uploadValidation';
import { CAD_IMPORT_MAX_BYTES } from '../cadImport';

export type GeoImportKind =
  | 'kmz'
  | 'kml'
  | 'geojson'
  | 'shapefile-zip'
  | 'shapefile-parts'
  | 'dwg';

export interface GeoImportPlan {
  kind: GeoImportKind;
  /** One file, except 'shapefile-parts' which carries the sibling set. */
  files: File[];
  /** Display label — the file name, or the shapefile basename group. */
  label: string;
  totalBytes: number;
}

export interface GeoImportRejection {
  file: File;
  reason: string;
}

export interface GeoImportPlanResult {
  plans: GeoImportPlan[];
  rejected: GeoImportRejection[];
}

/** For <input accept> and drop-zone filtering. */
export const GEO_IMPORT_ACCEPT = '.kmz,.kml,.geojson,.json,.zip,.shp,.dbf,.prj,.shx,.cpg,.dwg';

const SHAPEFILE_SIDECAR_EXTS = new Set(['dbf', 'prj', 'shx', 'cpg']);

const fileExt = (name: string): string => {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : '';
};

const fileStem = (name: string): string => {
  const idx = name.lastIndexOf('.');
  return (idx >= 0 ? name.slice(0, idx) : name).toLowerCase();
};

const mb = (bytes: number): string => `${Math.round(bytes / (1024 * 1024))} MB`;

/**
 * Group a selection into executable plans. Async because .zip archives are
 * sniffed (central directory scan) to distinguish zipped shapefiles from
 * arbitrary archives.
 */
export async function planGeoImports(files: readonly File[]): Promise<GeoImportPlanResult> {
  const plans: GeoImportPlan[] = [];
  const rejected: GeoImportRejection[] = [];

  // Pass 1 — group loose shapefile parts by basename.
  const shapefileGroups = new Map<string, File[]>();
  const loose: File[] = [];
  for (const file of files) {
    const ext = fileExt(file.name);
    if (ext === 'shp' || SHAPEFILE_SIDECAR_EXTS.has(ext)) {
      const key = fileStem(file.name);
      const group = shapefileGroups.get(key) ?? [];
      group.push(file);
      shapefileGroups.set(key, group);
    } else {
      loose.push(file);
    }
  }

  for (const [stem, group] of shapefileGroups) {
    const shp = group.find((f) => fileExt(f.name) === 'shp');
    if (!shp) {
      for (const file of group) {
        rejected.push({
          file,
          reason: `“${file.name}” needs its matching .shp file — select the whole shapefile set (or a .zip archive)`,
        });
      }
      continue;
    }
    const totalBytes = group.reduce((n, f) => n + f.size, 0);
    const oversize = group.find((f) => f.size > UPLOAD_MAX_FILE_BYTES);
    if (oversize) {
      rejected.push({ file: oversize, reason: `Larger than the ${mb(UPLOAD_MAX_FILE_BYTES)} import limit` });
      continue;
    }
    plans.push({ kind: 'shapefile-parts', files: group, label: `${stem}.shp`, totalBytes });
  }

  // Pass 2 — single-file plans.
  for (const file of loose) {
    const ext = fileExt(file.name);
    if (file.size <= 0) {
      rejected.push({ file, reason: 'File is empty' });
      continue;
    }
    if (file.size > UPLOAD_MAX_FILE_BYTES) {
      rejected.push({ file, reason: `Larger than the ${mb(UPLOAD_MAX_FILE_BYTES)} import limit` });
      continue;
    }

    switch (ext) {
      case 'kmz':
      case 'kml':
        plans.push({ kind: ext, files: [file], label: file.name, totalBytes: file.size });
        break;
      case 'geojson':
      case 'json':
        plans.push({ kind: 'geojson', files: [file], label: file.name, totalBytes: file.size });
        break;
      case 'dwg':
        if (file.size > CAD_IMPORT_MAX_BYTES) {
          rejected.push({ file, reason: `DWG files must be ${mb(CAD_IMPORT_MAX_BYTES)} or smaller` });
        } else {
          plans.push({ kind: 'dwg', files: [file], label: file.name, totalBytes: file.size });
        }
        break;
      case 'zip': {
        if (await zipContainsShp(file)) {
          plans.push({ kind: 'shapefile-zip', files: [file], label: file.name, totalBytes: file.size });
        } else {
          rejected.push({ file, reason: 'Archive contains no .shp — zip a shapefile set, or upload .kmz/.geojson directly' });
        }
        break;
      }
      default:
        rejected.push({ file, reason: `Unsupported file type .${ext || '?'} (supported: ${GEO_IMPORT_ACCEPT})` });
    }
  }

  return { plans, rejected };
}

async function zipContainsShp(file: File): Promise<boolean> {
  try {
    const { default: JSZip } = await import('jszip');
    const zip = await JSZip.loadAsync(await file.arrayBuffer());
    return Object.values(zip.files).some((f) => !f.dir && /\.shp$/i.test(f.name));
  } catch {
    return false;
  }
}

/* ── Execution ────────────────────────────────────────────────────────── */

export interface GeoImportExecutionContext {
  /** Site anchor for CAD drawings without an embedded georeference. */
  siteAnchor: { lat: number; lng: number } | null;
  signal?: AbortSignal;
}

export interface GeoImportExecution {
  plan: GeoImportPlan;
  parsed: ParsedGeoImport;
  /**
   * DWG only — the exact SiteLayers to commit (deterministic
   * `cad-{sha}-{handle}` ids feed the id-based `mergeCadImportedLayers`
   * path; re-deriving them from ParsedLayer rows would break re-import
   * idempotency). Row order matches `parsed.layers`.
   */
  cadLayers?: SiteLayer[];
}

function throwIfImportAborted(signal?: AbortSignal): void {
  if (!signal?.aborted) return;
  const error = new Error('Geometry import was cancelled.');
  error.name = 'AbortError';
  throw error;
}

export async function executeGeoImportPlan(
  plan: GeoImportPlan,
  ctx: GeoImportExecutionContext,
): Promise<GeoImportExecution> {
  throwIfImportAborted(ctx.signal);
  switch (plan.kind) {
    case 'kmz':
    case 'kml': {
      const { parseKmzOrKml } = await import('../kmzParser');
      const parsed = await parseKmzOrKml(plan.files[0]);
      throwIfImportAborted(ctx.signal);
      return { plan, parsed };
    }
    case 'geojson': {
      const { parseGeoJsonFile } = await import('./geoJsonImporter');
      const parsed = await parseGeoJsonFile(plan.files[0]);
      throwIfImportAborted(ctx.signal);
      return { plan, parsed };
    }
    case 'shapefile-zip': {
      const { parseShapefileZip } = await import('./shapefileParser');
      const parsed = await parseShapefileZip(plan.files[0]);
      throwIfImportAborted(ctx.signal);
      return { plan, parsed };
    }
    case 'shapefile-parts': {
      const { parseShapefileParts } = await import('./shapefileParser');
      const byExt = new Map(plan.files.map((f) => [fileExt(f.name), f]));
      const shp = byExt.get('shp');
      if (!shp) throw new Error('shapefile-parts plan without a .shp file');
      const parsed = await parseShapefileParts({
        shp,
        dbf: byExt.get('dbf'),
        prj: byExt.get('prj'),
        shx: byExt.get('shx'),
        cpg: byExt.get('cpg'),
      });
      throwIfImportAborted(ctx.signal);
      return {
        plan,
        parsed,
      };
    }
    case 'dwg':
      return executeDwgPlan(plan, ctx);
    default: {
      const exhaustive: never = plan.kind;
      throw new Error(`Unknown geo-import kind: ${String(exhaustive)}`);
    }
  }
}

async function executeDwgPlan(
  plan: GeoImportPlan,
  ctx: GeoImportExecutionContext,
): Promise<GeoImportExecution> {
  const file = plan.files[0];
  const { importDwgFile } = await import('../cadImport');
  const { cadImportToSiteLayers, defaultCadGeoreference, cadGeoreferenceAtSiteAnchor } =
    await import('../../lib/cad/cadImport');

  const payload = await importDwgFile(file, ctx.signal);
  const georef = defaultCadGeoreference(payload, ctx.siteAnchor)
    ?? (ctx.siteAnchor ? cadGeoreferenceAtSiteAnchor(payload, ctx.siteAnchor) : null);
  if (!georef) {
    return {
      plan,
      parsed: {
        fileName: file.name,
        boundaries: [],
        layers: [],
        warnings: [
          'Drawing has no embedded georeference and the site has no location to anchor it — set the site location first',
        ],
      },
    };
  }

  const cadLayers = cadImportToSiteLayers(payload, georef);
  const warnings = [...payload.warnings];
  if (georef.source === 'site-anchor') {
    warnings.push('Drawing has no embedded georeference — placed at the site anchor (adjust in Edit layers → CAD placement)');
  }

  // Adapt to the shared review shape; order stays 1:1 with cadLayers so the
  // commit path can map selected rows back to the prebuilt SiteLayers.
  const layers: ParsedLayer[] = cadLayers.map((layer) => ({
    name: layer.name,
    category: layer.category,
    geometry: layer.geometry,
    description: layer.description,
    folderPath: typeof layer.attributes?.cadLayer === 'string' ? [layer.attributes.cadLayer] : [],
    attributes: layer.attributes,
    sourceHint: 'cad-import',
  }));

  return {
    plan,
    parsed: { fileName: file.name, boundaries: [], layers, warnings },
    cadLayers,
  };
}
